import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Chip, IconButton, Stack, TextField, Button, Tooltip,
} from '@mui/material';
import {
  History as HistoryIcon, Print as PrintIcon, Search as SearchIcon,
} from '@mui/icons-material';
import PaginatedTable from '../components/common/PaginatedTable';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { cashApi } from '../api/cash.api';
import { CashSession } from '../types/cash';
import { printCierreTicket } from '../utils/cashReceipt';

const money = (n: any) => `S/ ${Number(n || 0).toFixed(2)}`;
const fdate = (d: any) => (d ? new Date(d).toLocaleString('es-PE') : '—');

const HistorialCajas: React.FC = () => {
  const [rows, setRows] = useState<CashSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [cajero, setCajero] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await cashApi.history({
        from: from || undefined,
        to: to || undefined,
        cajero: cajero || undefined,
      });
      setRows(res.data || []);
    } catch (err) {
      console.error('Error cargando historial:', err);
    } finally {
      setLoading(false);
    }
  }, [from, to, cajero]);

  useEffect(() => { load(); }, [load]);

  const reimprimir = async (session: CashSession) => {
    try {
      const res = await cashApi.getById(session.id);
      printCierreTicket(res.data.session, res.data.movements);
    } catch (err) {
      console.error('No se pudo cargar el detalle del cierre:', err);
      printCierreTicket(session);
    }
  };

  const diffChip = (v: number | null) => {
    const d = Number(v || 0);
    const cuadrada = Math.abs(d) < 0.01;
    return (
      <Chip
        size="small"
        label={cuadrada ? 'Cuadrada' : `${d > 0 ? '+' : ''}${money(d)}`}
        color={cuadrada ? 'success' : d < 0 ? 'error' : 'info'}
      />
    );
  };

  if (loading && rows.length === 0) return <LoadingSpinner />;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <HistoryIcon sx={{ fontSize: 32, color: '#7C3AED' }} />
        <Typography variant="h4">Historial y Auditoria de Cajas</Typography>
      </Box>
      <Typography variant="body2" sx={{ mb: 2, color: '#6B7280' }}>
        Todos los turnos cerrados con su arqueo completo. Filtra por fecha o cajero y reimprime cualquier cierre.
      </Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <TextField type="date" label="Desde" size="small" value={from} onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField type="date" label="Hasta" size="small" value={to} onChange={(e) => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField label="Cajero" size="small" value={cajero} onChange={(e) => setCajero(e.target.value)} placeholder="Nombre o email" />
        <Button variant="contained" startIcon={<SearchIcon />} onClick={load}>Filtrar</Button>
      </Stack>

      <PaginatedTable
        columns={[
          { id: 'id', label: 'Turno', minWidth: 70, format: (v: number) => `#${v}` },
          { id: 'closedByName', label: 'Cajero', format: (_v: any, r: CashSession) => r.closedByName || r.openedByName || '—' },
          { id: 'openedAt', label: 'Apertura', format: (v: string) => fdate(v) },
          { id: 'closedAt', label: 'Cierre', format: (v: string) => fdate(v) },
          { id: 'openingAmount', label: 'Inicial', format: (v: any) => money(v) },
          { id: 'salesEfectivo', label: 'Efectivo', format: (v: any) => money(v) },
          { id: 'salesTarjeta', label: 'Tarjeta', format: (v: any) => money(v) },
          { id: 'salesYape', label: 'Yape', format: (v: any) => money(v) },
          { id: 'totalSales', label: 'Ventas', format: (v: any) => money(v), sx: { fontWeight: 600 } },
          { id: 'totalIngresos', label: 'Ingresos', format: (v: any) => money(v) },
          { id: 'totalEgresos', label: 'Egresos', format: (v: any) => money(v) },
          { id: 'expectedCash', label: 'Esperado', format: (v: any) => money(v) },
          { id: 'countedCash', label: 'Contado', format: (v: any) => money(v) },
          { id: 'difference', label: 'Descuadre', format: (v: any) => diffChip(v) },
          {
            id: 'notes', label: 'Observaciones',
            format: (v: string) => v ? <Tooltip title={v}><span>{v.length > 24 ? v.slice(0, 24) + '…' : v}</span></Tooltip> : <span style={{ color: '#9CA3AF' }}>—</span>,
          },
        ]}
        rows={rows}
        searchFields={['closedByName', 'openedByName', 'notes']}
        exportFileName="historial-cajas"
        emptyMessage="No hay cierres de caja en el rango seleccionado."
        renderActions={(r: CashSession) => (
          <IconButton size="small" color="primary" title="Reimprimir cierre" onClick={() => reimprimir(r)}>
            <PrintIcon fontSize="small" />
          </IconButton>
        )}
      />
    </Box>
  );
};

export default HistorialCajas;
